import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { motion, AnimatePresence, type HTMLMotionProps } from 'framer-motion'

// ============================================
// Animated wrapper components for interactions
// ============================================

interface HoverScaleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  scale?: number
  duration?: number
}

export const HoverScale = forwardRef<HTMLDivElement, HoverScaleProps>(
  ({ scale = 1.05, duration = 0.2, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      whileHover={{ scale }}
      whileTap={{ scale: scale * 0.95 }}
      transition={{ duration }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
HoverScale.displayName = 'HoverScale'

interface HoverLiftProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  lift?: number
}

export const HoverLift = forwardRef<HTMLDivElement, HoverLiftProps>(
  ({ lift = -4, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      whileHover={{ y: lift }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
HoverLift.displayName = 'HoverLift'

// ============================================
// Pulse animation (breathing effect)
// ============================================

interface PulseProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  duration?: number
  scale?: [number, number]
}

export const Pulse = forwardRef<HTMLDivElement, PulseProps>(
  ({ duration = 2, scale = [1, 1.05], className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      animate={{
        scale: scale,
      }}
      transition={{
        duration,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
Pulse.displayName = 'Pulse'

// ============================================
// Glow pulse animation
// ============================================

interface GlowPulseProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  color?: string
  duration?: number
}

export const GlowPulse = forwardRef<HTMLDivElement, GlowPulseProps>(
  ({ color = 'rgba(0, 217, 255, 0.5)', duration = 2, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      animate={{
        boxShadow: [
          `0 0 10px ${color.replace('0.5', '0.2')}`,
          `0 0 20px ${color}`,
          `0 0 10px ${color.replace('0.5', '0.2')}`,
        ],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
GlowPulse.displayName = 'GlowPulse'

// ============================================
// Float animation (gentle up/down movement)
// ============================================

interface FloatProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  distance?: number
  duration?: number
  delay?: number
}

export const Float = forwardRef<HTMLDivElement, FloatProps>(
  ({ distance = 8, duration = 3, delay = 0, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      animate={{
        y: [-distance / 2, distance / 2, -distance / 2],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay,
      }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
Float.displayName = 'Float'

// ============================================
// Stagger children animation
// ============================================

interface StaggerChildrenProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  staggerDelay?: number
  initialY?: number
}

export const StaggerContainer = forwardRef<HTMLDivElement, StaggerChildrenProps>(
  ({ staggerDelay = 0.1, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { staggerChildren: staggerDelay },
        },
      }}
      initial="hidden"
      animate="visible"
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
StaggerContainer.displayName = 'StaggerContainer'

export const StaggerItem = forwardRef<HTMLDivElement, StaggerChildrenProps>(
  ({ initialY = 20, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      variants={{
        hidden: { opacity: 0, y: initialY },
        visible: {
          opacity: 1,
          y: 0,
          transition: { type: 'spring', stiffness: 300, damping: 24 },
        },
      }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
StaggerItem.displayName = 'StaggerItem'

// ============================================
// Shake animation (for errors/attention)
// ============================================

interface ShakeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  trigger?: boolean
  intensity?: number
}

export const Shake = forwardRef<HTMLDivElement, ShakeProps>(
  ({ trigger = false, intensity = 10, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      animate={trigger ? {
        x: [0, -intensity, intensity, -intensity, intensity, 0],
      } : {}}
      transition={{ duration: 0.5 }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
Shake.displayName = 'Shake'

// ============================================
// Bounce animation (for attention)
// ============================================

interface BounceProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  trigger?: boolean
}

export const Bounce = forwardRef<HTMLDivElement, BounceProps>(
  ({ trigger = false, className, children, ...props }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      animate={trigger ? {
        y: [0, -15, 0, -8, 0],
      } : {}}
      transition={{ duration: 0.6 }}
      {...(props as HTMLMotionProps<'div'>)}
    >
      {children}
    </motion.div>
  )
)
Bounce.displayName = 'Bounce'

// ============================================
// Fade transition wrapper
// ============================================

interface FadeTransitionProps {
  show: boolean
  children: ReactNode
  duration?: number
  className?: string
}

export function FadeTransition({ show, children, duration = 0.2, className }: FadeTransitionProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={className}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ============================================
// Slide transition wrapper
// ============================================

interface SlideTransitionProps {
  show: boolean
  children: ReactNode
  direction?: 'up' | 'down' | 'left' | 'right'
  distance?: number
  className?: string
}

const slideDirections = {
  up: { y: 20 },
  down: { y: -20 },
  left: { x: 20 },
  right: { x: -20 },
}

export function SlideTransition({
  show,
  children,
  direction = 'up',
  distance = 20,
  className,
}: SlideTransitionProps) {
  const directionValue = slideDirections[direction]
  const scaledDirection = Object.fromEntries(
    Object.entries(directionValue).map(([k, v]) => [k, (v / 20) * distance])
  )

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className={className}
          initial={{ opacity: 0, ...scaledDirection }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, ...scaledDirection }}
          transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ============================================
// Counter animation (number ticker)
// ============================================

interface AnimatedCounterProps {
  value: number
  duration?: number
  className?: string
}

export function AnimatedCounter({ value, duration = 0.5, className }: AnimatedCounterProps) {
  return (
    <motion.span
      key={value}
      className={className}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration }}
    >
      {value}
    </motion.span>
  )
}

// ============================================
// Presence animation wrapper
// ============================================

interface PresenceProps {
  children: ReactNode
  className?: string
}

export function Presence({ children, className }: PresenceProps) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
  )
}
